import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Handshake, Plus, FileText, Send, Copy, Trash2, CheckCircle2, Clock, AlertTriangle, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { propertiesAPI, clientsAPI } from '../lib/api';
import { moratoriumsAPI } from '../lib/moratoriumsAPI';

const STATUS_META = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700', icon: FileText },
  propose: { label: 'En attente de signature', color: 'bg-amber-100 text-amber-800', icon: Send },
  en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-800', icon: Clock },
  termine: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  rompu: { label: 'Rompu', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
};

const FREQUENCIES = [
  { v: 'weekly', label: 'Hebdomadaire' },
  { v: 'biweekly', label: 'Bimensuelle' },
  { v: 'monthly', label: 'Mensuelle' },
  { v: 'custom', label: 'Dates personnalisées' },
];

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso; }
};

export default function Moratoriums() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);

  // create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    tenant_id: '', property_id: '', total_amount: '', number_of_installments: 3,
    frequency: 'monthly', first_due_date: '', reason: '', late_fee_rate: 0, agreement_notes: '',
  });
  const [customAmounts, setCustomAmounts] = useState([]);
  const [customDates, setCustomDates] = useState([]);
  const [saving, setSaving] = useState(false);

  // share modal
  const [shareOpen, setShareOpen] = useState(false);
  const [shareData, setShareData] = useState(null);

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => {
    // keep custom arrays in sync with number
    const n = parseInt(form.number_of_installments || 0);
    if (!n) return;
    setCustomAmounts((prev) => Array.from({ length: n }, (_, i) => prev[i] ?? ''));
    setCustomDates((prev) => Array.from({ length: n }, (_, i) => prev[i] ?? ''));
  }, [form.number_of_installments]);

  async function fetchAll() {
    setLoading(true);
    try {
      const [m, t, p] = await Promise.all([
        moratoriumsAPI.list(),
        clientsAPI.getAll().catch(() => ({ data: [] })),
        propertiesAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setItems(m.data?.items || []);
      setTenants(Array.isArray(t.data) ? t.data : (t.data?.items || []));
      setProperties(Array.isArray(p.data) ? p.data : (p.data?.items || []));
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally { setLoading(false); }
  }

  const resetForm = () => {
    setForm({
      tenant_id: '', property_id: '', total_amount: '', number_of_installments: 3,
      frequency: 'monthly', first_due_date: '', reason: '', late_fee_rate: 0, agreement_notes: '',
    });
    setCustomAmounts([]); setCustomDates([]);
  };

  const handleCreate = async () => {
    if (!form.tenant_id || !form.property_id || !form.total_amount) {
      toast.error('Locataire, bien et montant total requis');
      return;
    }
    if (form.frequency !== 'custom' && !form.first_due_date) {
      toast.error('Date de 1ère échéance requise');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, total_amount: parseFloat(form.total_amount),
        number_of_installments: parseInt(form.number_of_installments),
        late_fee_rate: parseFloat(form.late_fee_rate || 0) };
      const nonEmpty = customAmounts.filter((a) => a !== '' && a !== null);
      if (nonEmpty.length === parseInt(form.number_of_installments)) {
        payload.custom_amounts = customAmounts.map((a) => parseFloat(a));
      }
      if (form.frequency === 'custom') {
        const dates = customDates.filter((d) => d);
        if (dates.length !== parseInt(form.number_of_installments)) {
          toast.error(`Veuillez renseigner les ${form.number_of_installments} dates d'échéance (fréquence personnalisée)`);
          setSaving(false);
          return;
        }
        payload.custom_dates = dates.map((d) => new Date(d).toISOString());
      }
      await moratoriumsAPI.create(payload);
      toast.success('Moratoire créé (brouillon)');
      setCreateOpen(false); resetForm(); fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleFinalize = async (m) => {
    if (!window.confirm("Finaliser et générer le lien de signature ? Le moratoire ne sera plus modifiable.")) return;
    try {
      const { data } = await moratoriumsAPI.finalize(m.id);
      const publicUrl = `${window.location.origin}${data.public_path}`;
      setShareData({ ...data, moratorium: m, public_url: publicUrl });
      setShareOpen(true);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleShare = (m) => {
    if (!m.signature_token) { toast.error('Pas de lien généré'); return; }
    const url = `${window.location.origin}/moratorium/sign/${m.signature_token}`;
    setShareData({ moratorium: m, public_url: url, tenant_name: m.tenant_name });
    setShareOpen(true);
  };

  const handleDownload = async (m) => {
    try {
      const resp = await moratoriumsAPI.downloadPdf(m.id);
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Moratoire_${m.tenant_name}_${m.id.slice(0, 8)}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erreur téléchargement'); }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Supprimer ce moratoire ?`)) return;
    try { await moratoriumsAPI.remove(m.id); toast.success('Supprimé'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleMarkPaid = async (m, index) => {
    if (!window.confirm(`Marquer l'échéance #${index} comme payée ?`)) return;
    try { await moratoriumsAPI.markInstallmentPaid(m.id, index); toast.success('Échéance marquée payée'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const totalFromCustom = useMemo(
    () => customAmounts.filter((a) => a !== '').reduce((s, a) => s + parseFloat(a || 0), 0),
    [customAmounts]
  );

  // Prefer clients with a rental link; fall back to clients with a phone number.
  const tenantOptions = useMemo(() => {
    const withLease = tenants.filter((t) => t.rented_property_id || t.property_id);
    return withLease.length ? withLease : tenants.filter((t) => t.phone);
  }, [tenants]);
  // Prefer properties linked to the selected tenant (via rented_property_id / lease).
  const propertiesForTenant = useMemo(() => {
    if (!form.tenant_id) return properties;
    const tenant = tenants.find((t) => t.id === form.tenant_id);
    const linkedId = tenant?.rented_property_id || tenant?.property_id;
    if (linkedId) {
      const linked = properties.filter((p) => p.id === linkedId);
      if (linked.length) return linked;
    }
    return properties;
  }, [form.tenant_id, tenants, properties]);

  const copyUrl = () => {
    if (!shareData?.public_url) return;
    navigator.clipboard.writeText(shareData.public_url);
    toast.success('Lien copié');
  };
  const shareWhatsApp = () => {
    if (!shareData?.public_url) return;
    const phone = (shareData.tenant_phone || '').replace(/[^0-9]/g, '');
    const wa = phone.length >= 8 ? (phone.startsWith('223') ? phone : `223${phone}`) : '';
    const msg = encodeURIComponent(
      `Bonjour ${shareData.tenant_name || ''},\n\nVeuillez consulter et signer électroniquement votre protocole de moratoire ici :\n${shareData.public_url}\n\nLien valable 30 jours.`
    );
    window.open(wa ? `https://wa.me/${wa}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank');
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="moratoriums-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#2B3A2C] flex items-center gap-2">
            <Handshake className="w-7 h-7 text-[#B85C38]" />
            Moratoires / Échéanciers
          </h1>
          <p className="text-slate-600 mt-1">
            Négociez un plan de remboursement pour les locataires en difficulté.
          </p>
        </div>
        <Button
          onClick={() => { resetForm(); setCreateOpen(true); }}
          className="bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-full"
          data-testid="create-moratorium-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau moratoire
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Moratoires enregistrés ({items.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-slate-500 py-8">Chargement…</p>
          ) : items.length === 0 ? (
            <p className="text-center text-slate-500 py-8">
              Aucun moratoire pour le moment. Créez-en un pour un locataire en difficulté.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Locataire</TableHead>
                  <TableHead>Bien</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Échéances</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((m) => {
                  const meta = STATUS_META[m.status] || STATUS_META.brouillon;
                  const Icon = meta.icon;
                  const paidCount = (m.installments || []).filter((i) => i.status === 'paye').length;
                  return (
                    <TableRow key={m.id} data-testid={`moratorium-row-${m.id}`}>
                      <TableCell className="font-medium">{m.tenant_name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{m.property_title}</TableCell>
                      <TableCell className="font-semibold">{fmt(m.total_amount)} XOF</TableCell>
                      <TableCell className="text-sm">
                        {paidCount}/{m.number_of_installments} payée(s)
                      </TableCell>
                      <TableCell>
                        <Badge className={meta.color}>
                          <Icon className="w-3 h-3 mr-1 inline" />
                          {meta.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="icon" onClick={() => handleDownload(m)} title="Télécharger PDF" data-testid={`download-mor-${m.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        {m.status === 'brouillon' && (
                          <Button variant="ghost" size="icon" onClick={() => handleFinalize(m)} title="Finaliser et envoyer" data-testid={`finalize-mor-${m.id}`}>
                            <Send className="w-4 h-4 text-[#B85C38]" />
                          </Button>
                        )}
                        {m.status === 'propose' && (
                          <Button variant="ghost" size="icon" onClick={() => handleShare(m)} title="Partager le lien" data-testid={`share-mor-${m.id}`}>
                            <Send className="w-4 h-4 text-amber-700" />
                          </Button>
                        )}
                        {(m.status === 'brouillon' || m.status === 'propose' || m.status === 'rompu') && (
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(m)} title="Supprimer" data-testid={`delete-mor-${m.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="moratorium-create-modal">
          <DialogHeader>
            <DialogTitle className="text-[#2B3A2C]">Nouveau moratoire</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-1">
              <Label>Locataire *</Label>
              <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                <SelectTrigger data-testid="mor-tenant-select"><SelectValue placeholder="Choisir le locataire" /></SelectTrigger>
                <SelectContent>
                  {tenantOptions.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} — {t.phone}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <Label>Bien concerné *</Label>
              <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="mor-property-select"><SelectValue placeholder="Choisir le bien" /></SelectTrigger>
                <SelectContent>
                  {propertiesForTenant.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant total impayé (XOF) *</Label>
              <Input type="number" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} data-testid="mor-total-input" />
            </div>
            <div>
              <Label>Nombre d'échéances *</Label>
              <Input type="number" min="1" value={form.number_of_installments} onChange={(e) => setForm({ ...form, number_of_installments: e.target.value })} data-testid="mor-count-input" />
            </div>
            <div>
              <Label>Fréquence</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="mor-freq-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((f) => <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.frequency !== 'custom' && (
              <div>
                <Label>Date 1ère échéance *</Label>
                <Input type="date" value={form.first_due_date} onChange={(e) => setForm({ ...form, first_due_date: e.target.value })} data-testid="mor-firstdate-input" />
              </div>
            )}
            <div>
              <Label>Pénalité de retard (%)</Label>
              <Input type="number" step="0.1" value={form.late_fee_rate} onChange={(e) => setForm({ ...form, late_fee_rate: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Motif</Label>
              <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Ex: Difficultés financières passagères" />
            </div>
            <div className="md:col-span-2">
              <Label>Mentions complémentaires (facultatif)</Label>
              <Textarea rows={3} value={form.agreement_notes} onChange={(e) => setForm({ ...form, agreement_notes: e.target.value })} />
            </div>

            {/* Flexible mode — custom amounts/dates */}
            <div className="md:col-span-2 border-t pt-3">
              <p className="text-sm font-semibold text-slate-700 mb-2">
                Ajuster chaque échéance (facultatif)
                {form.total_amount && customAmounts.filter((a) => a !== '').length === parseInt(form.number_of_installments) && (
                  <span className={`ml-2 text-xs ${Math.abs(totalFromCustom - parseFloat(form.total_amount)) < 1 ? 'text-emerald-600' : 'text-red-600'}`}>
                    Somme: {fmt(totalFromCustom)} / Total: {fmt(form.total_amount)}
                  </span>
                )}
              </p>
              <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                {Array.from({ length: parseInt(form.number_of_installments || 0) }).map((_, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <span className="w-10 text-xs text-slate-500">#{i + 1}</span>
                    {form.frequency === 'custom' && (
                      <Input type="date" value={customDates[i] || ''} onChange={(e) => {
                        const n = [...customDates]; n[i] = e.target.value; setCustomDates(n);
                      }} className="flex-1" />
                    )}
                    <Input type="number" placeholder="Montant auto" value={customAmounts[i] || ''}
                      onChange={(e) => { const n = [...customAmounts]; n[i] = e.target.value; setCustomAmounts(n); }}
                      className="flex-1" data-testid={`mor-amount-${i}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#2B3A2C] hover:bg-[#1e2a20] text-white" data-testid="mor-save-btn">
              {saving ? 'Enregistrement…' : 'Créer en brouillon'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share modal */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-lg" data-testid="moratorium-share-modal">
          <DialogHeader>
            <DialogTitle className="text-[#2B3A2C] flex items-center gap-2">
              <Send className="w-5 h-5 text-[#B85C38]" />
              Lien de signature du moratoire
            </DialogTitle>
          </DialogHeader>
          {shareData?.public_url && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Partagez ce lien sécurisé avec le locataire. Il permet de consulter et signer le protocole.
                Lien valable <b>30 jours</b>.
              </p>
              <div className="flex gap-2 items-center">
                <Input readOnly value={shareData.public_url} className="text-xs bg-slate-50" data-testid="mor-share-input" />
                <Button variant="outline" size="icon" onClick={copyUrl}><Copy className="w-4 h-4" /></Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={shareWhatsApp} className="bg-[#25D366] hover:bg-[#1fbe5b] text-white rounded-full" data-testid="mor-share-whatsapp">
                  <Send className="w-4 h-4 mr-2" /> WhatsApp
                </Button>
                <Button variant="outline" onClick={() => window.open(shareData.public_url, '_blank')} className="rounded-full">
                  Ouvrir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
